import { useEffect, useRef, useState } from 'react'
import { Box, Button, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useSnackbar } from 'notistack'
import { IQrScannerProps, QrScanner } from '@yudiel/react-qr-scanner'
import { useFormikContext } from 'formik'
import { useNavigate } from 'react-router-dom'

import {
  EScanToEarnProgressions,
  useScanToEarnContext,
} from '../form-wrapper/scan-to-earn-context.component'
import { HelperWrapper } from 'src/components/helper-wrapper'
import { PERMISSION_DENIED, ROUTES } from 'src/constants'
import { joinAbsRoute } from 'src/common/utils'
import { ETranslationNs, IMediaFileWithId } from 'src/types'
import { IScanToEarnCreateForm } from 'src/types/scan-to-earn.type'
import { base64ToFile } from 'src/common/utils'

import { useStyles } from '../styles'
import { useValidateBarcodeId } from 'src/hooks/scan-to-earn/use-validate-barcode-id.hook'
import { useGeoLocation } from 'src/hooks'

export const CameraProgression = () => {
  const [stopDecoding, setStopDecoding] = useState(false)
  const [cameraDenied, setCameraDenied] = useState(false)
  const [errorBlocked, setErrorBlocked] = useState(false)

  const scannerRef = useRef<HTMLDivElement>(null)

  const [t] = useTranslation(ETranslationNs.ScanForm)
  const navigate = useNavigate()
  const { values, setFieldValue } = useFormikContext<IScanToEarnCreateForm>()

  const { enqueueSnackbar } = useSnackbar()
  const { progression, setProgression, handleHowDoesItWorkOpen, isHowDoesItWorkOpen } =
    useScanToEarnContext()

  const { mutateAsync } = useValidateBarcodeId()

  const { coords, error: locationError } = useGeoLocation()

  const theme = useTheme()
  const classes = useStyles()

  useEffect(() => {
    if (isHowDoesItWorkOpen) {
      setStopDecoding(true)
    } else {
      setStopDecoding(false)
    }
  }, [isHowDoesItWorkOpen])

  useEffect(() => {
    if (coords) {
      setFieldValue('coords', coords)
    }
  }, [coords])

  const handleDecode = async (data: string) => {
    if (!values.coords.latitude || !values.coords.longitude) {
      return
    }

    if (data) {
      const validationResponse = await mutateAsync(data)

      if (validationResponse) {
        if (!validationResponse.isValid) {
          if (!errorBlocked) {
            enqueueSnackbar(validationResponse.messageFeedback, { variant: 'error' })
            setErrorBlocked(true)
            setTimeout(() => setErrorBlocked(false), 10000)
            return
          }
        } else {
          const video = scannerRef.current?.querySelector('video')

          if (video) {
            const canvas = document.createElement('canvas')

            canvas.width = video.videoWidth
            canvas.height = video.videoHeight

            const ctx = canvas.getContext('2d')
            ctx?.drawImage(video, 0, 0, canvas.width, canvas.height)

            const imageSrc = canvas.toDataURL('image/jpeg')

            const file = base64ToFile(imageSrc, `scan-${Date.now()}.jpg`, 'image/jpeg')

            const newMedia: IMediaFileWithId = {
              main_image: false,
              show_item: false,
              link_small: null,
              link_medium: null,
              link_large: null,
              title: `barcode-image-${data}`,
              link: imageSrc,
              type: 'image/jpeg',
              file,
              dragId: `id-${Date.now()}`,
            }

            setFieldValue('media', [newMedia])
          }

          setStopDecoding(true)
          setFieldValue('barcode_value', data)
          setProgression(EScanToEarnProgressions.ADD_MEDIA)
        }
        setTimeout(() => {
          setStopDecoding(false)
        }, 1000)
      }
    }
  }

  const handleError: IQrScannerProps['onError'] = (e) => {
    if (e.message === PERMISSION_DENIED) {
      setCameraDenied(true)
      return
    }

    enqueueSnackbar(t('scan-form-qr-code-error.v'), { variant: 'error' })
  }

  const handleBackClick = () => navigate(joinAbsRoute(ROUTES.USER, ROUTES.USER_DASHBOARD))

  const handleAddManuallyClick = () => {
    setProgression(EScanToEarnProgressions.ADD_MANUALLY)
  }

  if (progression !== EScanToEarnProgressions.CAMERA) {
    return null
  }

  return (
    <Box
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: '30px',
        alignItems: 'center',
        // justifyContent: 'space-between',
        // flex: 1,
      }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: '12px',
          alignItems: 'center',
        }}
      >
        <Box
          sx={{
            fontSize: '24px',
            fontWeight: 700,
            position: 'relative',
            display: 'inline-block',
          }}
        >
          <HelperWrapper t="scan-to-earn-header-title" ns={ETranslationNs.Navbar} />
          <Typography
            sx={{
              position: 'absolute',
              top: '2px',
              right: '-35px',
              fontStyle: 'italic',
              fontSize: '10px',
              fontWeight: 700,
              color: theme.palette.custom.primary,
            }}
          >
            ALPHA
          </Typography>
        </Box>

        <Button onClick={handleHowDoesItWorkOpen} variant="text" size="small">
          <HelperWrapper t="scan-to-earn-how-does-this-work-button-label" isColorInherited />
        </Button>

        <div ref={scannerRef} className={classes.cameraWrapper}>
          <QrScanner
            onDecode={handleDecode}
            onError={handleError}
            scanDelay={300}
            stopDecoding={stopDecoding}
            // constraints={{ facingMode: 'rear' }}
            constraints={{ facingMode: 'environment' }}
            containerStyle={{ width: '100%', height: '100%' }}
            videoStyle={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>

        {cameraDenied && (
          <Typography sx={{ textAlign: 'center', p: '0 8px' }} color="error">
            {t('scan-form-camera-error.v')}
          </Typography>
        )}
        {(!values.coords.latitude || !values.coords.longitude || locationError) && (
          <Typography sx={{ textAlign: 'center', p: '0 8px' }} color="error">
            {locationError}
          </Typography>
        )}
      </Box>

      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '30px',
          mb: '40px',
        }}
      >
        <Button onClick={handleBackClick} variant="contained" size="small">
          <HelperWrapper t="scan-to-earn-back-button-label" isColorInherited />
        </Button>
        {/* <Button variant="contained" size="small" onClick={handleAddManuallyClick}>
            <HelperWrapper t="scan-to-earn-enter-manually-button-label" isColorInherited />
          </Button> */}
      </Box>
    </Box>
  )
}
